import { useState } from 'react'
import { useTranslation } from '@kostbar/i18n'
import { Check, ChevronDown } from 'lucide-react'
import { DEVICE_PROTOCOLS, DEVICE_ROLES } from './edge-settings-utils'
import type { EdgeDeviceConfig, EdgeKioskConfig } from '@/api/types'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command'
import { Input } from '@/components/ui/input'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'

type DevicesTabProps = {
  devices: Array<EdgeDeviceConfig>
  normalizedKiosks: Array<EdgeKioskConfig>
  zoneOptions: Array<string>
  onUpdateDevice: (index: number, patch: Partial<EdgeDeviceConfig>) => void
  onAddDevice: () => void
  onRemoveDevice: (index: number) => void
  onSetDeviceKiosks: (index: number, next: Array<string>) => void
}

function AllowedKioskSelect({
  kiosks,
  value,
  onChange,
}: {
  kiosks: Array<EdgeKioskConfig>
  value: Array<string>
  onChange: (next: Array<string>) => void
}) {
  const { t: tRestaurants } = useTranslation('restaurants')
  const [open, setOpen] = useState(false)
  const selected = Array.isArray(value) ? value : []
  const selectedSet = new Set(selected)
  const selectedKiosks = kiosks.filter((kiosk) =>
    selectedSet.has(kiosk.stationId),
  )

  function toggleKiosk(kioskId: string) {
    const next = new Set(selected)
    if (next.has(kioskId)) {
      next.delete(kioskId)
    } else {
      next.add(kioskId)
    }
    const ordered = kiosks
      .map((kiosk) => kiosk.stationId)
      .filter((id) => next.has(id))
    onChange(ordered)
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          role="combobox"
          aria-expanded={open}
          className="h-auto min-h-9 w-full justify-between gap-2 px-3 py-2"
        >
          <div className="flex flex-wrap gap-1">
            {selectedKiosks.length > 0 ? (
              selectedKiosks.map((kiosk) => (
                <Badge
                  key={kiosk.stationId}
                  variant="secondary"
                  title={kiosk.name ? kiosk.stationId : undefined}
                  className={kiosk.name ? '' : 'font-mono'}
                >
                  {kiosk.name || kiosk.stationId}
                </Badge>
              ))
            ) : (
              <span className="text-xs text-muted-foreground">
                {tRestaurants('edgeSettings.kiosks.all')}
              </span>
            )}
          </div>
          <ChevronDown className="h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[260px] p-0" align="start">
        <Command>
          <CommandInput
            placeholder={tRestaurants('edgeSettings.kiosks.search')}
          />
          <CommandList>
            <CommandEmpty>
              {tRestaurants('edgeSettings.kiosks.noneFound')}
            </CommandEmpty>
            <CommandGroup>
              <CommandItem value="all" onSelect={() => onChange([])}>
                <span className="flex items-center gap-2">
                  {selected.length === 0 ? (
                    <Check className="h-4 w-4" />
                  ) : (
                    <span className="h-4 w-4" />
                  )}
                  {tRestaurants('edgeSettings.kiosks.all')}
                </span>
              </CommandItem>
              {kiosks.map((kiosk) => {
                const isSelected = selectedSet.has(kiosk.stationId)
                return (
                  <CommandItem
                    key={kiosk.stationId}
                    value={`${kiosk.name || ''} ${kiosk.stationId}`.trim()}
                    onSelect={() => toggleKiosk(kiosk.stationId)}
                  >
                    <span className="flex items-start gap-2">
                      {isSelected ? (
                        <Check className="h-4 w-4 mt-0.5" />
                      ) : (
                        <span className="h-4 w-4 mt-0.5" />
                      )}
                      <span className="flex flex-col">
                        <span>{kiosk.name || kiosk.stationId}</span>
                        {kiosk.name ? (
                          <span className="text-xs text-muted-foreground font-mono">
                            {kiosk.stationId}
                          </span>
                        ) : null}
                      </span>
                    </span>
                  </CommandItem>
                )
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}

export function DevicesTab({
  devices,
  normalizedKiosks,
  zoneOptions,
  onUpdateDevice,
  onAddDevice,
  onRemoveDevice,
  onSetDeviceKiosks,
}: DevicesTabProps) {
  const { t: tRestaurants } = useTranslation('restaurants')

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        {tRestaurants('edgeSettings.devices.subtitle')}
      </p>
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{tRestaurants('edgeSettings.devices.name')}</TableHead>
              <TableHead>{tRestaurants('edgeSettings.devices.role')}</TableHead>
              <TableHead>
                {tRestaurants('edgeSettings.devices.protocol')}
              </TableHead>
              <TableHead>
                {tRestaurants('edgeSettings.devices.cardReceiptPrint')}
              </TableHead>
              <TableHead>{tRestaurants('edgeSettings.devices.host')}</TableHead>
              <TableHead>{tRestaurants('edgeSettings.devices.port')}</TableHead>
              <TableHead>{tRestaurants('edgeSettings.devices.zone')}</TableHead>
              <TableHead>
                {tRestaurants('edgeSettings.devices.allowedKiosks')}
              </TableHead>
              <TableHead>
                {tRestaurants('edgeSettings.devices.priority')}
              </TableHead>
              <TableHead className="text-right"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {devices.length === 0 ? (
              <TableRow>
                <TableCell colSpan={10} className="text-muted-foreground">
                  {tRestaurants('edgeSettings.devices.empty')}
                </TableCell>
              </TableRow>
            ) : (
              devices.map((device, index) => (
                <TableRow key={`${device.id || 'new'}-${index}`}>
                  <TableCell>
                    <Input
                      value={device.name || ''}
                      onChange={(e) =>
                        onUpdateDevice(index, { name: e.target.value })
                      }
                    />
                  </TableCell>
                  <TableCell>
                    <Select
                      value={device.role}
                      onValueChange={(value) =>
                        onUpdateDevice(index, {
                          role: value,
                          protocol: DEVICE_PROTOCOLS[value]?.[0],
                          settings:
                            value === 'payment.card'
                              ? device.settings
                              : undefined,
                        })
                      }
                    >
                      <SelectTrigger>
                        <SelectValue
                          placeholder={tRestaurants(
                            'edgeSettings.devices.rolePlaceholder',
                          )}
                        />
                      </SelectTrigger>
                      <SelectContent>
                        {DEVICE_ROLES.map((role) => (
                          <SelectItem key={role} value={role}>
                            {role}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </TableCell>
                  <TableCell>
                    <Select
                      value={device.protocol || 'auto'}
                      onValueChange={(value) =>
                        onUpdateDevice(index, {
                          protocol: value === 'auto' ? undefined : value,
                        })
                      }
                      disabled={!device.role}
                    >
                      <SelectTrigger>
                        <SelectValue
                          placeholder={tRestaurants(
                            'edgeSettings.devices.protocolAuto',
                          )}
                        />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="auto">
                          {tRestaurants('edgeSettings.devices.protocolAuto')}
                        </SelectItem>
                        {(DEVICE_PROTOCOLS[device.role] ?? []).map(
                          (protocol) => (
                            <SelectItem key={protocol} value={protocol}>
                              {protocol}
                            </SelectItem>
                          ),
                        )}
                      </SelectContent>
                    </Select>
                  </TableCell>
                  <TableCell>
                    {device.role === 'payment.card' ? (
                      <Select
                        value={device.settings?.cardReceiptPrint || 'inherit'}
                        onValueChange={(value) => {
                          const nextCardReceiptPrint =
                            value === 'ecr' || value === 'terminal'
                              ? value
                              : undefined
                          onUpdateDevice(index, {
                            settings: nextCardReceiptPrint
                              ? {
                                  ...(device.settings || {}),
                                  cardReceiptPrint: nextCardReceiptPrint,
                                }
                              : undefined,
                          })
                        }}
                      >
                        <SelectTrigger>
                          <SelectValue
                            placeholder={tRestaurants(
                              'edgeSettings.devices.cardReceiptPrintPlaceholder',
                            )}
                          />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="inherit">
                            {tRestaurants(
                              'edgeSettings.devices.cardReceiptPrintOptions.inherit',
                            )}
                          </SelectItem>
                          <SelectItem value="ecr">
                            {tRestaurants(
                              'edgeSettings.devices.cardReceiptPrintOptions.ecr',
                            )}
                          </SelectItem>
                          <SelectItem value="terminal">
                            {tRestaurants(
                              'edgeSettings.devices.cardReceiptPrintOptions.terminal',
                            )}
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    ) : (
                      <span className="text-muted-foreground text-sm">-</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <Input
                      value={device.host || ''}
                      onChange={(e) =>
                        onUpdateDevice(index, { host: e.target.value })
                      }
                    />
                  </TableCell>
                  <TableCell>
                    <Input
                      type="number"
                      value={device.port ?? ''}
                      onChange={(e) =>
                        onUpdateDevice(index, {
                          port: e.target.value
                            ? Number(e.target.value)
                            : undefined,
                        })
                      }
                    />
                  </TableCell>
                  <TableCell>
                    <Select
                      value={
                        device.zones && device.zones.length > 0
                          ? device.zones[0]
                          : 'any'
                      }
                      onValueChange={(value) =>
                        onUpdateDevice(index, {
                          zones: value === 'any' ? [] : [value],
                        })
                      }
                      disabled={zoneOptions.length === 0}
                    >
                      <SelectTrigger>
                        <SelectValue
                          placeholder={tRestaurants(
                            'edgeSettings.devices.zoneAny',
                          )}
                        />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="any">
                          {tRestaurants('edgeSettings.devices.zoneAny')}
                        </SelectItem>
                        {zoneOptions.map((zone) => (
                          <SelectItem key={zone} value={zone}>
                            {zone}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </TableCell>
                  <TableCell>
                    {normalizedKiosks.length === 0 ? (
                      <span className="text-muted-foreground text-sm">
                        {tRestaurants('edgeSettings.kiosks.defineFirst')}
                      </span>
                    ) : (
                      <AllowedKioskSelect
                        kiosks={normalizedKiosks}
                        value={device.allowedKiosks || []}
                        onChange={(next) => onSetDeviceKiosks(index, next)}
                      />
                    )}
                  </TableCell>
                  <TableCell>
                    <Input
                      type="number"
                      value={device.priority ?? ''}
                      onChange={(e) =>
                        onUpdateDevice(index, {
                          priority: e.target.value
                            ? Number(e.target.value)
                            : undefined,
                        })
                      }
                    />
                  </TableCell>
                  <TableCell className="text-right">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onRemoveDevice(index)}
                    >
                      {tRestaurants('edgeSettings.devices.remove')}
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
      <Button variant="outline" onClick={onAddDevice}>
        {tRestaurants('edgeSettings.devices.add')}
      </Button>
    </div>
  )
}
